import React from 'react';
import { PageHeader, Card, CardContent, Button, Input, Alert } from '@/components/ui';
import { AuthService } from '@/services/AuthService';

export function ForgotPassword() {
  const [email, setEmail] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [sent, setSent] = React.useState(false);
  const [cooldown, setCooldown] = React.useState<number>(0);
  const svc = React.useMemo(() => new AuthService(), []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await svc.forgotPassword(email);
      setSent(true);
      setCooldown(prev => (prev > 0 ? prev : 60));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to request password reset');
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => {
      setCooldown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <PageHeader title="Forgot password" description="Enter your email to receive a reset link." />
        <Card>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-5">
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />

              {error && <Alert variant="error">{error}</Alert>}
              {sent && (
                <Alert variant="success">
                  If an account exists for this email, we sent a password reset link.
                </Alert>
              )}

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                loading={isLoading}
                disabled={isLoading || cooldown > 0}
                aria-disabled={isLoading || cooldown > 0}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Send reset link'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

